import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DialogService, DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { MessageService, ConfirmationService, PrimeNGConfig } from 'primeng/api';
import { Title } from '@angular/platform-browser';
import { NavigationService } from '../../../services/navigation/navigation.service';
import { CommonService } from '../../../services/common/common.service';
import { ToolsService } from '../../../services/tools/tools.service';
import { Column } from '../../interfaces/column.interface';
import { BannerTypesDataService, BannerTypesColumnsService } from '../../services';
import { DialogBannerTypeFormComponent } from './banner-type-form/banner-type-form.component';
import { DialogBannerTypeDetailsComponent } from './banner-type-details/banner-type-details.component';
import { BannerType, BannerTypesResult } from '../../interfaces';
import { FileUpload } from 'primeng/fileupload';

@Component({
  selector: 'app-banner-types',
  templateUrl: './banner-types.component.html',
  styleUrls: ['./banner-types.component.scss'],
  providers: [MessageService, ConfirmationService, DialogService],
})
export class BannerTypesComponent implements OnInit, OnDestroy {

  private bannerTypeAddDialog: DynamicDialogRef = new DynamicDialogRef();
  private bannerTypeEditDialog: DynamicDialogRef = new DynamicDialogRef();
  private bannerTypeDetailsDialog: DynamicDialogRef = new DynamicDialogRef();
  public bannerTypeToDelete: BannerType = {};
  public bannerTypes: BannerType[] = [];
  public bannerType: BannerType = {};
  public selectedBannerTypes: BannerType[] = [];
  public submitted: boolean = false;
  public parameters: {[param: string]: string | string[]} = {offset: '0', limit: '100'};
  public chunkSize: number = 200;
  public columns: Column[] = [];
  public isLoading: boolean = false;
  public isInitialized: boolean = false;
  public locale: string = (localStorage.language)?localStorage.language:'en';
  private _selectedColumns: Column[] = [];
  @ViewChild('importer', { static: false })
  public importer?: FileUpload;
  public importChooseLabel: string = 'Import';
  public importLabel: string = 'Import CSV';

  constructor(
    private translate: TranslateService,
    private navigation: NavigationService,
    private common: CommonService,
    private router: Router,
    private dialogService: DialogService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private primengConfig: PrimeNGConfig,
    private titleService: Title,
    private bannerTypesDataService: BannerTypesDataService,
    private bannerTypesColumnsService: BannerTypesColumnsService,
  ) {
    this.importChooseLabel = this.translate.instant('IMPORT');
    this.importLabel = this.translate.instant('IMPORTCSV');
  }

  ngOnInit(): void {
    console.log('BannerTypesComponent', 'ngOnInit');
    this.isInitialized = true;
    this.titleService.setTitle('Banner Types - Management');
    this.primengConfig.ripple = true;
    this.getBannerTypes();
  }

  ngOnDestroy(): void {
    console.log('BannerTypesComponent', 'ngOnDestroy');
    this.isInitialized = false;
  }

  getBannerTypes(): void {
    if (!this.isInitialized) {
      return;
    }
    if (parseInt(this.parameters.offset + '', 10) === 0) {
      this.isLoading = true;
    }
    this.bannerTypesDataService.getBannerTypes(this.parameters).subscribe((response: BannerTypesResult|any) => {
      this.isLoading = false;
      if (response !== null) {
        this.setColumns();
        if (parseInt(this.parameters.offset + '', 10) === 0) {
          this.bannerTypes = response.results;
        } else {
          this.bannerTypes = this.bannerTypes.concat(response.results);
        }
        if (response.inlineCount > this.bannerTypes.length) {
          this.parameters.offset = (parseInt(this.parameters.offset + '', 10) + parseInt(this.parameters.limit + '', 10)) + '';
          this.getBannerTypes();
        } else {
          this.parameters.offset = '0';
        }
      }
    }, (error: any) => {;
      this.isLoading = false;
      this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('BANNERTYPESNOTLOADED')});
    });
  }

  importBannerTypes(event: any): void {
    if (typeof event.currentFiles === 'undefined') {
      return;
    }
    if (event.currentFiles.length === 0) {
      return;
    }
    const inputFile = event.currentFiles[0];
    const reader = new FileReader();
    let rows: any[] = [];
    reader.onload = (e: any) => {
      if (typeof this.importer !== 'undefined') {
        this.importer.clear();
      }
      const text = <string>e.target.result;
      rows = ToolsService.csvToArray(this.columns, text, ',');
      if (rows.length > 0) {
        let proceed = 0;
        const j = rows.length;
        for (let i = 0; i < j; i += this.chunkSize) {
          this.isLoading = true;
          const chunk = rows.slice(i, i + this.chunkSize);
          if (typeof chunk[0].id === 'undefined') {
            this.bannerTypesDataService.addBannerTypes(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('BANNERTYPESCREATED')});
                if (proceed === j) {
                  this.getBannerTypes();
                }
              } else {
                this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('BANNERTYPESNOTCREATED')});
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('BANNERTYPESNOTCREATED') + ' ' + msg});
            });
          } else {
            this.bannerTypesDataService.updateBannerTypes(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('BANNERTYPESUPDATED')});
                if (proceed === j) {
                  this.getBannerTypes();
                }
              } else {
                this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('BANNERTYPESNOTUPDATED')});
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('BANNERTYPESNOTCREATED') + ' ' + msg});
            });
          }
        }
      }
    };
    reader.readAsText(inputFile);
  }

  setColumns() {
    if (this.columns.length > 0) {
      return;
    }
    this.columns = this.bannerTypesColumnsService.getColumns();
    for (let i = 0; i < this.columns.length; i++) {
      if (this.columns[i].show) {
        this._selectedColumns.push(this.columns[i]);
      }
    }
  }

  @Input()
  get selectedColumns(): any[] {
      return this._selectedColumns;
  }

  set selectedColumns(val: any[]) {
      // restore original order
      this._selectedColumns = this.columns.filter((col: any) => val.includes(col));
      this.updateColumns();
  }

  updateColumns() {
    for (let i = 0; i < this.columns.length; i++) {
      const show = this._selectedColumns.includes(this.columns[i]);
      this.columns[i].show = show;
      this.navigation.setParamValue('bannerTypes.' + this.columns[i].field + '.showFiled', show);
    }
  }

  deleteSelectedBannerTypes() {
    this.confirmationService.confirm({
      message: 'Are you sure you want to delete the selected Banner Types?',
      header: 'Confirm',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.bannerTypesDataService.deleteBannerTypes(this.selectedBannerTypes).subscribe((response) => {
          if (response === null) {
            this.messageService.clear();
            this.messageService.add({severity:'success', summary: 'Successful', detail: 'Banner Types Deleted', life: 3000});
            this.getBannerTypes();
          } else {
            this.messageService.clear();
            this.messageService.add({severity: 'error', summary: 'Error', detail: 'Banner Types not deleted'});
          }
        }, (error: any) => {
          this.messageService.clear();
          this.messageService.add({severity: 'error', summary: 'Error', detail: 'Banner Types not deleted'});
        });
        this.selectedBannerTypes = [];
      }
    });
  }

  exportPdf() {
    ToolsService.exportPdf(this.bannerTypes, this.columns, 'BannerTypes');
  }

  exportExcel() {
    ToolsService.exportExcel(this.bannerTypes, this.columns, 'BannerTypes');
  }

  exportCsv() {
    ToolsService.exportCsv(this.bannerTypes, this.columns, 'BannerTypes');
  }

  refresh() {
    this.bannerTypesDataService.clearCache();
    this.getBannerTypes();
  }

  bannerTypeAdd(): void {
    if (!this.navigation.getParamValue('bannerTypes.addInDialog', true)) {
      this.router.navigate(['/banner-types/add']);
      return;
    }
    this.bannerTypeAddDialog = this.dialogService.open(DialogBannerTypeFormComponent, {
      header: '', // this.translate.instant('NEWBANNERTYPE'),
      width: '80%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'add',
        bannerType: {id: null},
        users: this.bannerTypesColumnsService.users,
      },
    });

    this.bannerTypeAddDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({severity: 'info', summary: 'Success', detail: 'BannerType added'});
        this.getBannerTypes();
      }
    });
  }

  bannerTypeEdit(bannerType: BannerType): void {
    if (!this.navigation.getParamValue('bannerTypes.editInDialog', true)) {
      this.router.navigate(['/banner-types/edit/' + bannerType.id]);
      return;
    }
    this.bannerTypeEditDialog = this.dialogService.open(DialogBannerTypeFormComponent, {
      header: '', // this.translate.instant('EDITBANNERTYPE') + ' ' + bannerType.name,
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'edit',
        bannerType: bannerType,
        users: this.bannerTypesColumnsService.users,
      },
    });

    this.bannerTypeEditDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({severity: 'success', summary: 'Success', detail: 'BannerType updated'});
        this.getBannerTypes();
      }
    });
  }

  bannerTypeDetails(bannerType: BannerType): void {
    if (!this.navigation.getParamValue('bannerTypes.detailsInDialog', true)) {
      this.router.navigate(['/banner-types/details/' + bannerType.id]);
      return;
    }
    this.bannerTypeDetailsDialog = this.dialogService.open(DialogBannerTypeDetailsComponent, {
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        bannerType: bannerType,
      }
    });
  }

  bannerTypeDelete(bannerType: BannerType): void {
    this.bannerTypeToDelete = bannerType;
    this.messageService.clear();
    this.messageService.add({
      key: 'bannerTypeDelete',
      sticky: true,
      severity: 'warn',
      summary: 'Are you sure you want delete ' + bannerType.name + ' ?',
      detail: 'Confirm to proceed'
    });
  }

  onDeleteConfirm(): void {
    this.bannerTypesDataService.deleteBannerType(this.bannerTypeToDelete).subscribe((res: any) => {
      this.messageService.clear();
      this.messageService.add({severity: 'success', summary: 'Success', detail: 'Banner Type deleted'});
      this.getBannerTypes();
    }, (error: any) => {
      this.messageService.clear();
      this.messageService.add({severity: 'error', summary: 'Error', detail: 'Banner Type not deleted'});
    });
  }

  onDeleteReject(): void {
    this.bannerTypeToDelete = {};
    this.messageService.clear('bannerTypeDelete');
  }

}
